import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Modal, Typography } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { ModalV2 } from "components/Modal/ModalV2";
import {
  uploadServiceEvidence,
  deleteServiceEvidence,
} from "redux/actions/orders/ordersActions";
import { setSnackbar } from "redux/actions/search/searchActions";
import { useThemeMode } from "hooks/useThemeMode";

const EVIDENCE_SLOTS = [
  { type: "INITIAL", label: "Antes" },
  { type: "FINAL", label: "Después" },
  { type: "TICKET", label: "Ticket" },
  { type: "REMOTE", label: "Fuga alert" },
];

// Formatos que acepta el endpoint de evidencias
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Devuelve una imagen por tipo de evidencia. Se prefiere la registrada en el
 * servicio sobre la que viene de la nota del operador; entre imagenes del
 * mismo origen se usa la ultima del array.
 */
const getLatestByType = (evidences) => {
  return (evidences || []).reduce((acc, img) => {
    if (!img?.type) return acc;
    const current = acc[img.type];
    if (!current || img.source !== "NOTE" || current.source === "NOTE") acc[img.type] = img;
    return acc;
  }, {});
};

/**
 * Solo se pueden eliminar las evidencias registradas en el servicio (con `id`).
 * Las fotos que vienen de la nota del operador (`source: "NOTE"`) no se pueden
 * borrar desde aqui.
 */
const canDeleteEvidence = (img) => !!img?.id && img?.source !== "NOTE";

export const OrderEvidence = ({ evidences, serviceId }) => {
  const dispatch = useDispatch();
  const { isDark } = useThemeMode();
  const { isLoadingComp } = useSelector(({ search }) => search);
  const [selectedImage, setSelectedImage] = useState(null);
  const [pendingType, setPendingType] = useState(null);
  const [evidenceToDelete, setEvidenceToDelete] = useState(null);
  const inputRef = useRef(null);

  const loading = !!isLoadingComp?.loadingServiceEvidence;
  const latestByType = getLatestByType(evidences);

  const openFilePicker = (type) => {
    if (loading) return;
    if (inputRef.current) inputRef.current.value = "";
    setPendingType(type);
    inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && !ALLOWED_TYPES.includes(file.type)) {
      dispatch(setSnackbar("Formato no permitido. Usa imágenes JPG, PNG o WEBP."));
    } else if (file && pendingType && serviceId) {
      const formData = new FormData();
      formData.append("type", pendingType);
      formData.append("images", file, file.name);
      dispatch(uploadServiceEvidence(serviceId, formData));
    }
    setPendingType(null);
  };

  const handleDelete = (e, img) => {
    e.stopPropagation();
    if (loading) return;
    setEvidenceToDelete(img);
  };

  const closeDeleteModal = () => {
    if (loading) return;
    setEvidenceToDelete(null);
  };

  const confirmDelete = () => {
    if (loading || !canDeleteEvidence(evidenceToDelete) || !serviceId) return;
    dispatch(
      deleteServiceEvidence(serviceId, evidenceToDelete.id, () =>
        setEvidenceToDelete(null)
      )
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <ImageIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Evidencias
        </Typography>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        hidden
        onChange={handleFileChange}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1,
        }}
      >
        {EVIDENCE_SLOTS.map(({ type, label }) => {
          const img = latestByType[type];

          return (
            <Box key={type}>
              {img ? (
                <Box
                  onClick={() => setSelectedImage(img.url)}
                  sx={{
                    cursor: "pointer",
                    position: "relative",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: 1,
                    borderColor: "divider",
                    "&:hover img": { opacity: 0.85 },
                  }}
                >
                  <Box
                    component="img"
                    src={img.url}
                    alt={label}
                    sx={{
                      width: "100%",
                      height: 90,
                      objectFit: "cover",
                      display: "block",
                      transition: "opacity .2s",
                    }}
                  />
                  {canDeleteEvidence(img) && (
                    <Box
                      onClick={(e) => handleDelete(e, img)}
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        width: 26,
                        height: 26,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isDark ? "rgba(229, 57, 53, 0.2)" : "#FDE4EB",
                        cursor: loading ? "default" : "pointer",
                        opacity: loading ? 0.5 : 1,
                        "&:hover": { bgcolor: isDark ? "rgba(229, 57, 53, 0.35)" : "#F9CBD8" },
                      }}
                    >
                      <DeleteIcon sx={{ color: "error.main", fontSize: 18 }} />
                    </Box>
                  )}
                  {img.source === "NOTE" && (
                    // La foto de la nota no se puede borrar; se permite subir la evidencia del servicio
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        openFilePicker(type);
                      }}
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        width: 26,
                        height: 26,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "background.paper",
                        cursor: loading ? "default" : "pointer",
                        opacity: loading ? 0.5 : 0.9,
                        "&:hover": { opacity: loading ? 0.5 : 1 },
                      }}
                    >
                      <AddIcon sx={{ color: "primary.main", fontSize: 18 }} />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  onClick={() => openFilePicker(type)}
                  sx={{
                    cursor: loading ? "default" : "pointer",
                    height: 90,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    border: "1px dashed",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: loading ? 0.6 : 1,
                    transition: "background-color .2s",
                    "&:hover": { bgcolor: loading ? "action.hover" : "action.selected" },
                  }}
                >
                  <AddIcon sx={{ color: "primary.main", fontSize: 32 }} />
                </Box>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 0.5,
                  color: "text.secondary",
                  fontWeight: 600,
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Modal open={!!selectedImage} onClose={() => setSelectedImage(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
          }}
          onClick={() => setSelectedImage(null)}
        >
          <Box
            component="img"
            src={selectedImage}
            alt="Evidencia ampliada"
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              borderRadius: 2,
              boxShadow: 6,
            }}
          />
        </Box>
      </Modal>

      <ModalV2
        open={!!evidenceToDelete}
        onClose={closeDeleteModal}
        onSubmit={confirmDelete}
        justConfirm
        showSnackbar={false}
        title="¿Seguro que quieres eliminar esta evidencia?"
        confirmText="ELIMINAR EVIDENCIA"
        confirmColor="info"
        isLoading={loading}
      >
        <Typography
          variant="body1"
          sx={{ textAlign: "center", width: "100%", color: "text.secondary" }}
        >
          La imagen se eliminará permanentemente y no podrás recuperarla
        </Typography>
      </ModalV2>
    </Box>
  );
};
